export type FenderStyle = 'tono' | 'contrasto';
export type WheelFinish = 'nero' | 'crema' | 'cromo';

export interface Paint {
  slug: string;
  name: string;
  hex: string;
  toneOnTone: string;
}

export const PAINTS: readonly Paint[] = [
  { slug: 'rosa-shocking', name: 'Rosa Shocking', hex: '#ef5aa0', toneOnTone: '#e34e8f' },
  { slug: 'rosa-tipsy', name: 'Rosa Tipsy', hex: '#ea83a2', toneOnTone: '#e07595' },
  { slug: 'menta-spritz', name: 'Menta Spritz', hex: '#8fd3b6', toneOnTone: '#82c8aa' },
  { slug: 'crema-aperitivo', name: 'Crema Aperitivo', hex: '#ecdcbc', toneOnTone: '#e3d1ad' },
  { slug: 'azzurro-riviera', name: 'Azzurro Riviera', hex: '#3a8fd6', toneOnTone: '#3482c4' },
  { slug: 'nero-notte', name: 'Nero Notte', hex: '#17191f', toneOnTone: '#0d0e11' },
];

export const DEFAULT_BODY = PAINTS[0].hex;

export const FENDER_STYLES: readonly { value: FenderStyle; name: string }[] = [
  { value: 'tono', name: 'Tono su tono' },
  { value: 'contrasto', name: 'Contrasto' },
];

export interface WheelFinishSpec {
  value: WheelFinish;
  name: string;
  color: string;
  metalness: number;
  roughness: number;
}

export const WHEEL_FINISHES: readonly WheelFinishSpec[] = [
  { value: 'nero', name: 'Nero', color: '#131417', metalness: 0.3, roughness: 0.38 },
  { value: 'crema', name: 'Crema', color: '#eee2c8', metalness: 0, roughness: 0.36 },
  { value: 'cromo', name: 'Cromo', color: '#f0f2f5', metalness: 0.9, roughness: 0.18 },
];

const HEX = /^#[0-9a-f]{6}$/;
const CONTRAST_CREAM = '#f1e6d2';
const CONTRAST_NERO = '#16181e';
const LIGHT_BODY_LUMINANCE = 0.45;

function channels(hex: string): [number, number, number] {
  if (!HEX.test(hex)) throw new Error(`Paint colour must be lowercase #rrggbb, got "${hex}"`);
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
}

function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function derivedToneOnTone(hex: string): string {
  const [r, g, b] = channels(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  const h = d === 0 ? 0 : max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  const L = Math.max(0, l - 0.047);
  const S = s * 0.887;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = L - c / 2;
  const sector: [number, number, number][] = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ];
  return `#${sector[Math.floor(h) % 6].map((v) => Math.round((v + m) * 255).toString(16).padStart(2, '0')).join('')}`;
}

export function paintByHex(hex: string): Paint | undefined {
  return PAINTS.find((p) => p.hex === hex);
}

export function fenderColor(body: string, style: FenderStyle): string {
  if (style === 'contrasto') return luminance(body) > LIGHT_BODY_LUMINANCE ? CONTRAST_NERO : CONTRAST_CREAM;
  return paintByHex(body)?.toneOnTone ?? derivedToneOnTone(body);
}

export function wheelFinish(value: WheelFinish): WheelFinishSpec {
  const spec = WHEEL_FINISHES.find((w) => w.value === value);
  if (!spec) throw new Error(`Unknown wheel finish "${value}"`);
  return spec;
}
